// Tech metadata: maps a technology label to its logo slug (simpleicons.org)
// and a display group. Used to render grouped, icon-bearing stack chips.

export type TechGroup = 'Frontend' | 'Backend & Cloud' | 'Practices'

interface TechInfo {
  slug?: string // simpleicons.org slug; omit when no clean brand icon exists
  group: TechGroup
}

/**
 * Labels that are concepts/practices rather than branded products, so they use
 * the generic glyph instead of a brand logo. Every chip still shows an icon.
 */
export const CONCEPT_LABELS = new Set(
  [
    'Recoil',
    'Styled System',
    'AG Grid',
    'WebForms',
    'Blockchain',
    'REST APIs',
    'Open Graph',
    'GDS',
    'Mob Programming',
    'Trunk-Based Development',
    'Test-Driven Development',
    'SQL',
    'Sitecore',
    'WCAG AA Accessibility',
    'Mobile App Development',
  ].map((s) => s.toLowerCase()),
)

// Keyed by a normalised (lowercased) label.
const TECH: Record<string, TechInfo> = {
  // Frontend
  react: { slug: 'react', group: 'Frontend' },
  'react.js': { slug: 'react', group: 'Frontend' },
  'react native': { slug: 'react', group: 'Frontend' },
  'next.js': { slug: 'nextdotjs', group: 'Frontend' },
  typescript: { slug: 'typescript', group: 'Frontend' },
  javascript: { slug: 'javascript', group: 'Frontend' },
  'es6+': { slug: 'javascript', group: 'Frontend' },
  redux: { slug: 'redux', group: 'Frontend' },
  'redux toolkit': { slug: 'redux', group: 'Frontend' },
  recoil: { group: 'Frontend' },
  'react query': { slug: 'reactquery', group: 'Frontend' },
  'tailwind css': { slug: 'tailwindcss', group: 'Frontend' },
  tailwind: { slug: 'tailwindcss', group: 'Frontend' },
  'shadcn/ui': { slug: 'shadcnui', group: 'Frontend' },
  'chakra ui': { slug: 'chakraui', group: 'Frontend' },
  mui5: { slug: 'mui', group: 'Frontend' },
  'styled system': { group: 'Frontend' },
  'styled components': { slug: 'styledcomponents', group: 'Frontend' },
  'ag grid': { group: 'Frontend' },
  html5: { slug: 'html5', group: 'Frontend' },
  'html5+': { slug: 'html5', group: 'Frontend' },
  css3: { slug: 'css3', group: 'Frontend' },
  scss: { slug: 'sass', group: 'Frontend' },
  sass: { slug: 'sass', group: 'Frontend' },
  less: { slug: 'less', group: 'Frontend' },
  jquery: { slug: 'jquery', group: 'Frontend' },
  'angular.js': { slug: 'angular', group: 'Frontend' },
  ionic: { slug: 'ionic', group: 'Frontend' },
  webforms: { group: 'Frontend' },
  'react context api': { slug: 'react', group: 'Frontend' },
  vite: { slug: 'vite', group: 'Frontend' },
  webpack: { slug: 'webpack', group: 'Frontend' },
  gulp: { slug: 'gulp', group: 'Frontend' },
  bootstrap: { slug: 'bootstrap', group: 'Frontend' },
  'chrome extension': { slug: 'googlechrome', group: 'Frontend' },

  // Backend & Cloud
  'node.js': { slug: 'nodedotjs', group: 'Backend & Cloud' },
  node: { slug: 'nodedotjs', group: 'Backend & Cloud' },
  strapi: { slug: 'strapi', group: 'Backend & Cloud' },
  '.net framework': { slug: 'dotnet', group: 'Backend & Cloud' },
  'asp.net': { slug: 'dotnet', group: 'Backend & Cloud' },
  'c#': { slug: 'csharp', group: 'Backend & Cloud' },
  php: { slug: 'php', group: 'Backend & Cloud' },
  sql: { group: 'Backend & Cloud' },
  wordpress: { slug: 'wordpress', group: 'Backend & Cloud' },
  drupal: { slug: 'drupal', group: 'Backend & Cloud' },
  sitecore: { group: 'Backend & Cloud' },
  aws: { slug: 'amazonwebservices', group: 'Backend & Cloud' },
  amplify: { slug: 'awsamplify', group: 'Backend & Cloud' },
  lambda: { slug: 'awslambda', group: 'Backend & Cloud' },
  s3: { slug: 'amazons3', group: 'Backend & Cloud' },
  dynamodb: { slug: 'amazondynamodb', group: 'Backend & Cloud' },
  ec2: { slug: 'amazonec2', group: 'Backend & Cloud' },
  cognito: { slug: 'amazoncognito', group: 'Backend & Cloud' },
  vercel: { slug: 'vercel', group: 'Backend & Cloud' },
  blockchain: { group: 'Backend & Cloud' },
  zilliqa: { slug: 'zilliqa', group: 'Backend & Cloud' },
  'rest apis': { group: 'Backend & Cloud' },
  'open graph': { group: 'Backend & Cloud' },

  // Practices / tooling
  playwright: { slug: 'playwright', group: 'Practices' },
  figma: { slug: 'figma', group: 'Practices' },
  gds: { group: 'Practices' },
  'mob programming': { group: 'Practices' },
  'trunk-based development': { group: 'Practices' },
  'test-driven development': { group: 'Practices' },
  'wcag aa accessibility': { group: 'Practices' },
  'mobile app development': { group: 'Practices' },
  'zilliqa api': { slug: 'zilliqa', group: 'Backend & Cloud' },
}

export const GROUP_ORDER: TechGroup[] = [
  'Frontend',
  'Backend & Cloud',
  'Practices',
]

export interface StackChip {
  label: string
  slug?: string
  /** True when the item is a concept/practice with no brand logo. */
  generic?: boolean
}

export interface StackGroup {
  group: TechGroup
  items: StackChip[]
}

/**
 * Turn an arbitrary tag into a candidate simpleicons.org slug, e.g.
 * "Next.js" -> "nextjs", "React Native" -> "reactnative", "C#" -> "csharp".
 * Simple Icons slugs are lowercase alphanumerics with a few special-char rules;
 * this covers the common cases. Unknown guesses that 404 fall back to the
 * generic glyph at render time.
 */
export function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/\+/g, 'plus')
    .replace(/#/g, 'sharp')
    .replace(/\./g, 'dot')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '')
}

/**
 * Resolve a single tag: curated slug wins; otherwise, if it's not a known
 * concept, guess a slug so unknown tags still attempt a real logo (with the
 * generic glyph as the safety net on image error).
 */
function resolveTag(label: string): StackChip {
  const key = label.toLowerCase()
  const info = TECH[key]
  const isConcept = CONCEPT_LABELS.has(key)

  // Curated entry with a slug — use it.
  if (info?.slug) return { label, slug: info.slug, generic: false }

  // Known concept (no logo by design) — generic glyph.
  if (isConcept || info) return { label, slug: undefined, generic: true }

  // Unknown tag — attempt an auto-derived slug; may render or 404→generic.
  const guess = slugify(label)
  return guess
    ? { label, slug: guess, generic: false }
    : { label, slug: undefined, generic: true }
}

/** Resolve labels into chips (slug + generic flag) preserving the given order. */
export function resolveChips(labels: string[]): StackChip[] {
  return labels.map(resolveTag)
}

/** Bucket a flat tech list into ordered groups, preserving first-seen order. */
export function groupStack(stack: string[]): StackGroup[] {
  const buckets = new Map<TechGroup, StackChip[]>()

  for (const label of stack) {
    const key = label.toLowerCase()
    const group: TechGroup = TECH[key]?.group ?? 'Frontend'
    if (!buckets.has(group)) buckets.set(group, [])
    buckets.get(group)!.push(resolveTag(label))
  }

  return GROUP_ORDER.filter((g) => buckets.has(g)).map((g) => ({
    group: g,
    items: buckets.get(g)!,
  }))
}
